// @ts-nocheck
/* eslint-disable */
import { loadImageData } from '@repo/utils';
import { cv, setup } from './setup';

function hsv255ToRgb(h: number, s: number, v: number): { r: number, g: number, b: number } {
  let R = 0, G = 0, B = 0;
  let C = 0, X = 0, Y = 0, Z = 0;
  let i = 0;
  let H = h * 2; // Equivalent to (float)(h + h)
  const S = s / 255.0;
  const V = v / 255.0;

  if (S === 0) {
    R = G = B = V;
  } else {
    H = H / 60;
    i = Math.floor(H);
    C = H - i;
    X = V * (1 - S);
    Y = V * (1 - S * C);
    Z = V * (1 - S * (1 - C));
    switch (i) {
      case 0:
        R = V;
        G = Z;
        B = X;
        break;
      case 1:
        R = Y;
        G = V;
        B = X;
        break;
      case 2:
        R = X;
        G = V;
        B = Z;
        break;
      case 3:
        R = X;
        G = Y;
        B = V;
        break;
      case 4:
        R = Z;
        G = X;
        B = V;
        break;
      case 5:
        R = V;
        G = X;
        B = Y;
        break;
    }
  }


  return { r: Math.round(R * 255), g: Math.round(G * 255), b: Math.round(B * 255) };
}

function rgbToHsv255(r: number, g: number, b: number): { h: number, s: number, v: number } {
  r = r / 255;
  g = g / 255;
  b = b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, v = max;

  if (max !== 0) {
    s = 1 - (min / max);
  }

  if (max === min) {
    h = 0;
  } else if (max === r && g >= b) {
    h = 60 * ((g - b) / (max - min));
  } else if (max === r && g < b) {
    h = 60 * ((g - b) / (max - min)) + 360;
  } else if (max === g) {
    h = 60 * ((b - r) / (max - min)) + 120;
  } else if (max === b) {
    h = 60 * ((r - g) / (max - min)) + 240;
  }

  h = h * 0.5;
  v = v * 255;
  s = s * 255;

  return { h, s, v };
}

class ChangeColorItem {
  private hNew: number;
  private sNew: number;
  private vNew: number;

  constructor(
    private color: number,
    private maxV: number,
    private minV: number,
    private avgV: number,
    private stddevV: number
  ) {
    this.init();
  }

  private init() {
    const r = (this.color >> 16) & 0xFF;
    const g = (this.color >> 8) & 0xFF;
    const b = this.color & 0xFF;
    const { h, s, v } = rgbToHsv255(r, g, b);
    this.hNew = h;
    this.sNew = s;
    this.vNew = v;
  }

  h(): number {
    return this.hNew;
  }

  s(): number {
    return this.sNew;
  }

  v(): number {
    return this.vNew;
  }

  mapV(inputV: number): number {
    const dv = this.vNew - this.avgV;
    const v = inputV + dv;
    return Math.min(255, Math.max(0, Math.round(v)));
  }
}



export async function changeColor(url: string, colorHex: string) {
  if (!cv) {
    await setup();
  }

  const imageData = await loadImageData(url);
  console.log('Starting color change process...');

  // 将十六进制颜色转换为 HSV
  const [r, g, b] = hexToRgb(colorHex);
  const [h, s] = rgbToHsv(r, g, b);
  console.log(`Converted RGB to HSV: H=${h}, S=${s}`);

  const width = imageData.width;
  const height = imageData.height;
  const data = imageData.data;
  const totalPixels = width * height;

  // 使用 TypedArray 直接操作，避免 OpenCV Mat 的开销
  const vArray = new Uint8Array(totalPixels);
  const maskArray = new Uint8Array(totalPixels);

  // 单次遍历：提取 V 值和 mask
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  const hist = new Array(256).fill(0);

  for (let i = 0; i < totalPixels; i++) {
    const idx = i * 4;
    const alpha = data[idx + 3];

    if (alpha === 255) {
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      const v = Math.max(r, g, b); // rgbToV255 内联
      
      vArray[i] = v;
      maskArray[i] = 255;
      
      // 同时计算统计信息
      sum += v;
      sumSq += v * v;
      hist[v]++;
      count++;
    } else {
      vArray[i] = 0;
      maskArray[i] = 0;
    }
  }

  // 计算统计信息
  const mean = sum / count;
  const variance = (sumSq / count) - (mean * mean);
  const stddev = Math.sqrt(variance);

  // 归一化直方图
  for (let i = 0; i < 256; i++) {
    hist[i] /= count;
  }

  const imgSt: ImageStatics = { 
    avg: Math.min(255, Math.max(0, mean)),
    min: Math.min(255, Math.max(0, mean - 3 * stddev)),
    max: Math.min(255, Math.max(0, mean + 3 * stddev)),
    stddev: stddev,
    hist: hist,
    isAccumlate: false 
  };

  const cci = new ChangeColorItem(hexToInt(colorHex), imgSt.max, imgSt.min, imgSt.avg, imgSt.stddev);

  // 预计算 LUT
  const hNew = cci.h();
  const sNew = cci.s();
  const vLut = new Uint8Array(256);
  for (let i = 0; i < 256; i++) {
    vLut[i] = cci.mapV(i);
  }

  // 预计算 HSV 到 RGB 的转换表
  const rgbLut = new Uint8Array(256 * 3);
  for (let i = 0; i < 256; i++) {
    const { r, g, b } = hsv255ToRgb(hNew, sNew, vLut[i]);
    rgbLut[i * 3] = r;
    rgbLut[i * 3 + 1] = g;
    rgbLut[i * 3 + 2] = b;
  }

  // 单次遍历应用颜色变换
  for (let i = 0; i < totalPixels; i++) {
    const v = vArray[i];
    const idx = i * 4;
    const lutIdx = v * 3;
    
    data[idx] = rgbLut[lutIdx];
    data[idx + 1] = rgbLut[lutIdx + 1];
    data[idx + 2] = rgbLut[lutIdx + 2];
  }

  console.log('Color change process completed.');

  return new ImageData(data, width, height);
}

function hexToRgb(hex) {
  let bigint = parseInt(hex.substring(1), 16);
  let r = (bigint >> 16) & 255;
  let g = (bigint >> 8) & 255;
  let b = bigint & 255;
  return [r, g, b];
}

function hexToInt(hex) {
  return parseInt(hex.substring(1), 16);
}

function rgbToHsv(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const delta = max - min;
  let h, s, v = max;

  s = max === 0 ? 0 : delta / max;

  if (max === min) {
    h = 0; // achromatic
  } else {
    switch (max) {
      case r:
        h = (g - b) / delta + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / delta + 2;
        break;
      case b:
        h = (r - g) / delta + 4;
        break;
    }
    h /= 6;
  }

  return [h * 360, s]; // 返回色调 (h) 和饱和度 (s)
}


